import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';

import { useAuth } from '../../src/context/AuthContext';
import { api } from '../../src/api/client';
import ContentContainer from '../../src/components/ContentContainer';
import Button from '../../src/components/Button';
import Input from '../../src/components/Input';
import InlineConfirm from '../../src/components/InlineConfirm';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { titleCase } from '../../src/utils/format';

/** Minimum password length enforced on the client */
const MIN_PASSWORD_LENGTH = 8;

/** AdminSettings - admin profile, password change, session management, and theme toggle */
export default function AdminSettings() {
  const { colors, themeMode, setThemeMode } = useTheme();
  const styles = useStyles(colors);
  const { user, logout, logoutAll } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSignOutAllConfirm, setShowSignOutAllConfirm] = useState(false);

  /** Validates inputs and submits password change request */
  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) { setError('Please fill in all fields'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < MIN_PASSWORD_LENGTH) { setError('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[^A-Za-z0-9]/.test(newPassword)) {
      setError('Password must include upper, lower, number, and special character'); return;
    }

    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await api('/api/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } });
      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) { setError(err.message || 'Failed to change password'); }
    finally { setLoading(false); }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer role="admin">
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Admin Profile</Text>
        <View style={styles.infoRow}>
          <Text style={styles.label}>Email</Text>
          <Text style={styles.value}>{user?.email}</Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.label}>Name</Text>
          <Text style={styles.value}>{titleCase(user?.name)}</Text>
        </View>
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Change Password</Text>
        {error ? <Text style={styles.error}>{error}</Text> : null}
        {success ? <Text style={styles.success}>{success}</Text> : null}
        <Input label="Current Password" icon="lock-closed-outline" value={currentPassword} onChangeText={setCurrentPassword} secureTextEntry />
        <Input label="New Password" icon="lock-closed-outline" value={newPassword} onChangeText={setNewPassword} secureTextEntry />
        <Input label="Confirm New Password" icon="lock-closed-outline" value={confirmPassword} onChangeText={setConfirmPassword} secureTextEntry />
        <Button title="Update Password" onPress={handleChangePassword} loading={loading} style={{ marginTop: spacing.sm }} />
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Session Management</Text>
        <Button title="Sign Out All Devices" onPress={() => setShowSignOutAllConfirm(true)} variant="outline" style={{ marginBottom: spacing.sm }} />
        <InlineConfirm
          message="This will sign you out of all devices."
          visible={showSignOutAllConfirm}
          onConfirm={logoutAll}
          onCancel={() => setShowSignOutAllConfirm(false)}
          confirmLabel="Sign Out All"
          variant="danger"
        />
        <Button title="Sign Out" onPress={logout} variant="outline" />
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Appearance</Text>
        <View style={{ flexDirection: 'row', gap: spacing.sm }}>
          {(['light', 'dark', 'system'] as const).map(mode => (
            <TouchableOpacity
              key={mode}
              onPress={() => setThemeMode(mode)}
              style={{
                paddingHorizontal: 14,
                paddingVertical: 6,
                borderRadius: 16,
                backgroundColor: themeMode === mode ? colors.primary : colors.surfaceSecondary,
                borderWidth: 1,
                borderColor: themeMode === mode ? colors.primary : colors.border,
              }}
            >
              <Text style={{
                fontSize: fontSize.xs,
                color: themeMode === mode ? colors.white : colors.textSecondary,
                fontWeight: fontWeight.medium,
              }}>
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.lg },
  card: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  label: { fontSize: fontSize.md, color: c.textSecondary },
  value: { fontSize: fontSize.md, fontWeight: fontWeight.medium, color: c.text },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.md, textAlign: 'center' },
  success: { fontSize: fontSize.sm, color: '#059669', backgroundColor: '#05966910', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.md, textAlign: 'center' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
